import React, { useCallback, useMemo, useState } from 'react';

import IconButton from '@material-ui/core/IconButton';
import SvgIcon from '@material-ui/core/SvgIcon';
import classnames from 'classnames';
import { TEACHER_ASSIGNMENT_V2_INSIGHTS_URL } from 'config/urls';
import { find, findIndex, first, get, isNil, last, map } from 'lodash';

import { GRADE_TAB } from 'pages/Teachers/Insights/constants';
import {
  default as StudentWorkSelectInput,
  getLabelForWork
} from 'pages/Teachers/shared/StudentWorkSelectInput';
import { colors } from 'theme/palette';
import { openInNewTab, reverse, stringifyParams } from 'utils/urls';

import Button from 'components/Button';
import Dialog from 'components/Dialog';
import StudentWorkTasksButtons from 'components/StudentWorkTasksButtons';
import Typography from 'components/Typography';

import { ReactComponent as ArrowWithBorder } from './assets/arrow-with-border.svg';
import styles from './styles.module.scss';

// This component differs from components/StudentWorkDialog in the way we get the active task
const StudentWorkDialog = ({
  onClose,
  initialTaskIndex,
  initialWorkIndex,
  studentWork,
  assignmentId,
  trackerId
}) => {
  const [taskIndex, setTaskIndex] = useState(initialTaskIndex);
  const [workIndex, setWorkIndex] = useState(initialWorkIndex);

  const activeTask = find(studentWork.tasks, { index: taskIndex }) || {};
  const activeWork = get(activeTask.work, workIndex, { emptyWork: true });

  const studentName = studentWork.student.is_teacher_demo_student
    ? `Exemplar - ${studentWork.student.preferred_name}`
    : studentWork.student.name;

  const tasksButtonsElements = useMemo(
    () =>
      studentWork.tasks.map((task) => ({
        label: task.task_index + 1,
        key: task.id,
        value: task.index,
        isActive: task.index === taskIndex
      })),
    [studentWork.tasks, taskIndex]
  );

  const taskWorks = useMemo(
    () =>
      map(activeTask.work, (work, index) => ({
        key: work.id,
        label: getLabelForWork(work),
        value: index
      })),
    [activeTask.work]
  );

  const onNextClick = useCallback(() => {
    const activeTaskArrayIndex = findIndex(studentWork.tasks, {
      index: taskIndex
    });

    const nextTask = studentWork.tasks[activeTaskArrayIndex + 1];

    let nextIndex = nextTask?.index;

    if (isNil(nextTask)) {
      nextIndex = first(studentWork.tasks).index;
    }

    setTaskIndex(nextIndex);
    setWorkIndex(0);
  }, [studentWork.tasks, taskIndex]);

  const onPreviousClick = useCallback(() => {
    const activeTaskArrayIndex = findIndex(studentWork.tasks, {
      index: taskIndex
    });

    const previousTask = studentWork.tasks[activeTaskArrayIndex - 1];

    let prevIndex = previousTask?.index;

    if (isNil(previousTask)) {
      prevIndex = last(studentWork.tasks).index;
    }

    setTaskIndex(prevIndex);
    setWorkIndex(0);
  }, [studentWork.tasks, taskIndex]);

  const redirectToFeedback = useCallback(() => {
    const pageUrl = reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
      trackerInstanceSectionId: assignmentId
    });

    const params = stringifyParams({
      activeTab: GRADE_TAB,
      studentId: studentWork.student.id,
      taskId: activeTask.id,
      workId: activeWork.id,
      isFeedbackOpened: true
    });
    openInNewTab(`${pageUrl}?${params}`);
  }, [assignmentId, studentWork.student.id, activeTask.id, activeWork.id]);

  return (
    <Dialog open onClose={onClose} classes={{ paper: styles.paper }}>
      <img
        src={activeWork.s3_work_url}
        className={styles.image}
        alt={activeWork.original_filename}
      />
      {studentWork.tasks.length > 1 && (
        <IconButton
          className={classnames(styles.left, styles.arrow)}
          onClick={onPreviousClick}
        >
          <SvgIcon component={ArrowWithBorder} />
        </IconButton>
      )}
      {studentWork.tasks.length > 1 && (
        <IconButton
          className={classnames(styles.right, styles.arrow)}
          onClick={onNextClick}
        >
          <SvgIcon component={ArrowWithBorder} />
        </IconButton>
      )}
      <div className={styles.footer}>
        <Typography
          variant="H-TEXT-3"
          color={colors.blue1}
          className={styles.studentName}
        >
          {studentName}
        </Typography>
        <Button fullWidth color="pink" onClick={redirectToFeedback}>
          Feedback
        </Button>
        <StudentWorkTasksButtons
          classes={{ root: styles.tasksButtons }}
          elements={tasksButtonsElements}
          onChange={(element) => {
            setTaskIndex(element.value);
            setWorkIndex(0);
          }}
        />
        <StudentWorkSelectInput
          value={workIndex}
          onChange={(value) => {
            setWorkIndex(value);
          }}
          options={taskWorks}
          disabled={get(activeWork, 'emptyWork', false)}
        />
      </div>
    </Dialog>
  );
};

export default StudentWorkDialog;
